import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';

import { ReactComponent as CalendarDots } from '../../../../assets/icons/v3/calendar-dots.svg';
import { ReactComponent as Files } from '../../../../assets/icons/v3/files.svg';
import StyledTable from '../../../../shared/components/v3/Table/StyledTable/StyledTable';
import { CompanyContext } from '../../../../shared/context/company/companyContext';
import { HEALTH_REPORT_TABLE_COLUMNS } from '../../../../shared/handlers/tableColumns';
import { IHealthReportList } from '../../../../shared/interfaces/reports';
import Company from '../../../../shared/services/api/companies/companies';
import { TRANSLATIONS_REPORTS } from '../../../../shared/translations/reports';
import { sortArray } from '../../../../shared/utils/utils';

import '../ReportsTable.scss';

export const HealthReportsTable = () => {
  const [reports, setReports] = useState<IHealthReportList[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const { companyData, isLoadingCompanyData } = useContext(CompanyContext);

  const { t } = useTranslation();

  const isValidCompany = useCallback(() => {
    return companyData?.id !== '' && companyData?.locale;
  }, [companyData]);

  const fetchHealthsReports = useCallback(async () => {
    setLoading(true);
    try {
      const companyReports = await Company.getCompanyHealthReports(companyData.id);
      if (companyReports) {
        const lastGeneratedReport = companyReports.reports.find((report) => report.status === 'closed');
        if (lastGeneratedReport) {
          lastGeneratedReport.isLastGenerated = true;
        }
        setReports(companyReports.reports);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [companyData.id]);

  const sortTableData = (columnKey: string, isAscending: boolean) => {
    let sortedData: IHealthReportList[] = [];
    if (columnKey === 'period') {
      sortedData = sortArray([...reports], 'endDate', isAscending) as IHealthReportList[];
    } else {
      sortedData = sortArray([...reports], columnKey, isAscending) as IHealthReportList[];
    }
    setReports(sortedData);
  };

  useEffect(() => {
    if (isValidCompany() && !isLoadingCompanyData) {
      fetchHealthsReports();
    }
  }, [companyData, isLoadingCompanyData, isValidCompany, fetchHealthsReports]);

  return (
    <div className="reports-tabs-container">
      <div className="flex flex-row items-center justify-between mb-4">
        <div className="reports-title">{t(TRANSLATIONS_REPORTS.default.health.title)}</div>
        <div className="reports-info flex flex-row">
          <div className="reports-frecuency flex flex-row items-center gap-3">
            <CalendarDots className="calendar-dots" />
            <span className="reports-frecuency-label">
              <Trans i18nKey={TRANSLATIONS_REPORTS.frequency.quarterly} components={{ b: <b></b> }} />
            </span>
          </div>
          <div className="reports-format flex flex-row items-center gap-3">
            <Files />
            <span className="reports-format-label">
              <Trans i18nKey={TRANSLATIONS_REPORTS.formatWeb} components={{ b: <b></b> }} />
            </span>
          </div>
        </div>
      </div>
      <div className="reports-description mb-16">{t(TRANSLATIONS_REPORTS.default.health.description)}</div>
      <StyledTable
        data={reports}
        columns={HEALTH_REPORT_TABLE_COLUMNS}
        sortData={sortTableData}
        error={error}
        errorAction={fetchHealthsReports}
        loading={loading || isLoadingCompanyData}
        emptyCard={{
          primaryText: TRANSLATIONS_REPORTS.table.emptyState.title,
          secondaryText: TRANSLATIONS_REPORTS.table.emptyState.description
        }}
      />
    </div>
  );
};

export default HealthReportsTable;
